/* Public-event display vocabulary and formatting.
   Mirrors backend/src/constants/eventVocabulary.js — the type list is the
   curation skill's twelve verbatim (a drift here silently mismatches the
   import validator), and the four party buckets roll the pipeline's raw
   codes into something a citizen can read. */
import { formatPartyDisplay, resolveParty } from '../../constants/parties';

export const EVENT_TYPES = [
  'town_hall',
  'party_meeting',
  'convention_assembly',
  'campaign_event',
  'volunteer_action',
  'fundraiser',
  'debate_forum',
  'rally_protest',
  'advocacy_briefing',
  'training',
  'social',
  'other',
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

export const EVENT_TYPE_LABELS: Record<string, string> = {
  town_hall: 'Town hall',
  party_meeting: 'Party meeting',
  convention_assembly: 'Convention / assembly',
  campaign_event: 'Campaign event',
  volunteer_action: 'Volunteer action',
  fundraiser: 'Fundraiser',
  debate_forum: 'Debate / forum',
  rally_protest: 'Rally / protest',
  advocacy_briefing: 'Advocacy briefing',
  training: 'Training',
  social: 'Social',
  other: 'Other',
};

export const eventTypeLabel = (type?: string | null): string =>
  (type && EVENT_TYPE_LABELS[type]) || 'Event';

export const PARTY_BUCKETS = [
  { key: 'nonpartisan', label: 'Nonpartisan' },
  { key: 'democratic', label: 'Democratic' },
  { key: 'republican', label: 'Republican' },
  { key: 'unaffiliated_minor', label: 'Unaffiliated & minor' },
] as const;

export type PartyBucketKey = (typeof PARTY_BUCKETS)[number]['key'];

const BUCKET_BY_PARTY_ID: Record<string, PartyBucketKey> = {
  non_partisan: 'nonpartisan',
  democratic: 'democratic',
  republican: 'republican',
  unaffiliated: 'unaffiliated_minor',
  independent: 'unaffiliated_minor',
  libertarian: 'unaffiliated_minor',
  green: 'unaffiliated_minor',
  no_labels: 'unaffiliated_minor',
  american_constitution: 'unaffiliated_minor',
  unity: 'unaffiliated_minor',
  approval_voting: 'unaffiliated_minor',
  center: 'unaffiliated_minor',
  forward: 'unaffiliated_minor',
};

/** Bucket for a stored host_party code; null when unstated/unknown. */
export function partyBucketOf(raw?: string | null): PartyBucketKey | null {
  const entry = resolveParty(raw);
  if (!entry || entry.id === 'unknown') return null;
  return BUCKET_BY_PARTY_ID[entry.id] ?? null;
}

/** Readable party label for a row chip; empty when the party is unstated. */
export function partyLabelOf(raw?: string | null): string {
  if (!raw || !String(raw).trim()) return '';
  const entry = resolveParty(raw);
  if (!entry || entry.id === 'unknown') return '';
  return formatPartyDisplay(raw);
}

/* ── Time + date formatting ────────────────────────────────────────────────
   Events store event_date + start_time + end_time separately and are always
   America/Denver (the import validator rejects any other timezone), so the
   display string is composed here rather than stored. */

/**
 * Parse an event's calendar date to LOCAL midnight.
 *
 * `event_date` is a Postgres DATE, and node-pg hands it back as a JS Date, so
 * it arrives over JSON as a full timestamp ("2026-08-15T06:00:00.000Z") rather
 * than "2026-08-15". Taking the first ten characters covers both shapes.
 *
 * Constructing from parts (not `new Date(string)`) is deliberate: parsing an
 * ISO date string yields UTC midnight, which renders as the PREVIOUS day for
 * anyone west of Greenwich — including every Colorado reader.
 */
const parseDate = (value: string): Date => {
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  // No silent fallbacks here: a bad value must surface as an Invalid Date
  // rather than quietly rendering some other day (this masked a bug where
  // every event showed the 1st of the month).
  if (!y || !m || !d) return new Date(NaN);
  return new Date(y, m - 1, d);
};

const formatClock = (time: string): string => {
  const [hStr, mStr] = time.split(':');
  const hours = Number(hStr);
  const minutes = Number(mStr);
  if (Number.isNaN(hours)) return '';
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return minutes ? `${hour12}:${String(minutes).padStart(2, '0')} ${suffix}` : `${hour12} ${suffix}`;
};

/** "6:30 – 8 PM", "6:30 PM", or "" — the AM/PM is dropped from the start when it matches the end. */
export function formatEventTime(startTime?: string | null, endTime?: string | null): string {
  const start = startTime ? formatClock(startTime) : '';
  const end = endTime ? formatClock(endTime) : '';
  if (!start) return end ? `Ends ${end}` : '';
  if (!end) return start;
  const startSuffix = start.slice(-2);
  const endSuffix = end.slice(-2);
  const startShown = startSuffix === endSuffix ? start.slice(0, -3) : start;
  return `${startShown} – ${end}`;
}

/** "Tue 6:30 – 8 PM" for a row's meta line. */
export function formatEventWhen(event: { event_date: string; start_time?: string | null; end_time?: string | null }): string {
  const date = parseDate(event.event_date);
  const time = formatEventTime(event.start_time, event.end_time);
  if (Number.isNaN(date.getTime())) return time;
  const weekday = date.toLocaleDateString(undefined, { weekday: 'short' });
  return time ? `${weekday} ${time}` : weekday;
}

/** Month abbreviation + day for the date block. */
export function eventDateParts(isoDate: string): { month: string; day: string } {
  const date = parseDate(isoDate);
  if (Number.isNaN(date.getTime())) return { month: '—', day: '' };
  return {
    month: date.toLocaleDateString(undefined, { month: 'short' }),
    day: String(date.getDate()),
  };
}

/** Date-group heading, generated relative to today (never hardcoded). */
export function eventGroupLabel(isoDate: string, today = new Date()): string {
  const date = parseDate(isoDate);
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((date.getTime() - startOfToday.getTime()) / 86_400_000);
  // Week boundaries are Sunday-based, matching how people read a calendar.
  const daysToWeekEnd = 6 - startOfToday.getDay();
  if (days <= daysToWeekEnd) return 'This week';
  if (days <= daysToWeekEnd + 7) return 'Next week';
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/** "Saturday, August 15" — the long form for the detail modal. */
export function formatEventFullDate(isoDate: string): string {
  const date = parseDate(isoDate);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

/** Google Maps link for an in-person event, or null when there's no address. */
export function eventDirectionsUrl(event: {
  venue?: string | null;
  address?: string | null;
  city?: string | null;
}): string | null {
  const query = [event.venue, event.address, event.city, 'CO'].filter(Boolean).join(', ');
  if (!event.address && !event.venue) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/** Where an event happens, in one phrase. */
export function formatEventPlace(event: {
  format: string;
  venue?: string | null;
  city?: string | null;
}): string {
  if (event.format === 'virtual') return 'Online';
  const parts = [event.venue, event.city].filter(Boolean);
  const place = parts.join(' · ');
  if (event.format === 'hybrid') return place ? `${place} · Online too` : 'Online';
  return place || 'Location TBA';
}
